import { useCreditAccess, useCreditSummary } from 'features/credits/hooks/use-credits';
import { useGetUsersQuery } from 'features/iam/hooks/use-iam';
import type { IamData } from 'features/iam/services/user-service';
import { usePlanSeats } from 'features/subscription/hooks/use-seats';
import { planSeatsOfUser } from 'features/subscription/utils/seat-pools';
import { useTokenUsage } from 'features/usage/hooks/use-usage';
import { seatUsageMeters } from 'features/usage/utils/seat-usage-meters';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { mapIamUserToTeamsMember, teamsPeopleApiFilter } from './map-iam-user-to-teams-member';
import {
  filterTeamsPeople,
  sortTeamsPeopleByCredits,
  type TeamsPerson,
  type TeamsPersonStatus,
  type TeamsUsageFilter,
} from './teams-mock-data';
import {
  isNearLimit,
  PAGE_SIZE,
  planUsagePage,
  rankSeatHolders,
  searchPosition,
  usageCounts,
  type UsagePagePlan,
} from './teams-usage-paging';

type UsagePeopleParams = {
  startDate: string;
  endDate: string;
  filter: TeamsUsageFilter;
  query: string;
  page: number;
};

/**
 * One page of the organization's people, the seat holders with the most AI credits used first.
 * Credits come from the seats and tokens from the picked range. Daily-limit, underused and
 * shared-spend figures have no bulk API, so they stay empty.
 */
export function useTeamsUsagePeople({ startDate, endDate, filter, query, page }: UsagePeopleParams) {
  const isAdmin = useCommonStore((state) => state.isAdmin);
  const isOrgAdmin = useCommonStore((state) => state.isOrgAdmin);
  const currentUserId = useCommonStore((state) => state.user?.id);
  const canRead = isAdmin || isOrgAdmin;

  const { planSeats, isLoading: isLoadingSeats } = usePlanSeats(true);
  const tokenQuery = useTokenUsage({ startDate, endDate, scope: 'org' }, canRead);
  const { data: creditSummary } = useCreditSummary();
  const totalQuery = useGetUsersQuery({ page: 0, pageSize: 1, enabled: canRead });

  const pools = planSeats.flatMap((entry) => (entry.pool ? [entry.pool] : []));
  const creditMeters = seatUsageMeters(pools).filter(
    (meter) => meter.meterKey === 'ai-credits' && meter.userId
  );
  const tokenEntries = (tokenQuery.data?.breakdowns.by_user ?? []).flatMap((entry) =>
    entry.user_id ? [{ userId: entry.user_id, tokens: entry.total_tokens }] : []
  );
  const tokensOf = new Map(tokenEntries.map((entry) => [entry.userId, entry.tokens]));
  const orgTokens = tokenQuery.data?.totals.total_tokens ?? 0;

  const holders = rankSeatHolders(
    creditMeters.map((meter) => ({
      userId: meter.userId,
      used: meter.used,
      included: meter.included,
      tokens: tokensOf.get(meter.userId) ?? 0,
    }))
  );
  const holderIds = new Set(holders.map((holder) => holder.userId));
  const tokenUsersWithoutSeat = tokenEntries.filter(
    (entry) => entry.tokens > 0 && !holderIds.has(entry.userId)
  ).length;

  const counts = usageCounts(holders, totalQuery.data?.totalCount ?? 0, tokenUsersWithoutSeat);
  const plan = planUsagePage(holders, filter, page, counts, tokenUsersWithoutSeat);

  const searchText = query.trim();
  const isSearching = searchText !== '';
  const isReady = canRead && !isLoadingSeats && !tokenQuery.isLoading && !totalQuery.isLoading;
  const seatsQuery = useGetUsersQuery({
    page: 0,
    pageSize: PAGE_SIZE,
    filter: { userIds: plan.seatIds },
    enabled: isReady && !isSearching && plan.seatIds.length > 0,
  });
  const pageQuery = useGetUsersQuery({
    ...pageUsersRequest(searchText, page, plan.others),
    enabled: isReady && (isSearching || plan.others !== null),
  });

  const shownUsers = isSearching
    ? pageQuery.data?.data ?? []
    : [
        ...usersOfSeats(seatsQuery.data?.data ?? [], plan.seatIds),
        ...usersWithoutSeat(pageQuery.data?.data ?? [], plan.others, filter, holderIds, tokensOf),
      ];
  const { data: creditAccess } = useCreditAccess(
    shownUsers.map((user) => user.itemId),
    canRead
  );
  const blocked = new Set(creditAccess?.blockedUserIds);

  const people = shownUsers.map((user): TeamsPerson => {
    const member = mapIamUserToTeamsMember(user, currentUserId);
    const meter = creditMeters.find((item) => item.userId === member.id);
    const tokens = tokensOf.get(member.id) ?? 0;
    const seat = planSeatsOfUser(planSeats, member.id);

    let status: TeamsPersonStatus = null;
    if (meter && isNearLimit(meter)) {
      status = 'near_limit';
    } else if (!seat && creditSummary?.enabled && !blocked.has(member.id)) {
      status = 'shared_credits_only';
    }

    return {
      id: member.id,
      initials: member.initials,
      name: member.name,
      email: member.email,
      isYou: member.isYou,
      plan: seat?.name ?? null,
      creditsUsed: meter ? meter.used : null,
      creditsLimit: meter ? meter.included : null,
      sharedCreditsLabel: null,
      tokensThisWeek: tokens,
      percentOfAll: orgTokens > 0 ? (tokens / orgTokens) * 100 : 0,
      // Everyone may use the shared credits unless an admin switched them off.
      creditAccess: !blocked.has(member.id),
      lastLogin: member.lastLogin,
      status,
    };
  });

  const position = isSearching ? searchPosition(page, pageQuery.data?.totalCount ?? 0) : plan;

  return {
    // IAM pages a search by name, so the chip is applied to the page that came back.
    people: isSearching ? sortTeamsPeopleByCredits(filterTeamsPeople(people, filter, '')) : people,
    counts,
    page: position.page,
    pageCount: position.pageCount,
    totalCount: position.totalCount,
    pageStart: position.start,
    isTruncated: position.isTruncated,
    period: tokenQuery.data?.period,
    // Tokens decide the Not using filter, so a new range shows the skeleton until they arrive.
    isLoading:
      isLoadingSeats ||
      tokenQuery.isLoading ||
      totalQuery.isLoading ||
      seatsQuery.isLoading ||
      pageQuery.isLoading,
    error: totalQuery.error ?? seatsQuery.error ?? pageQuery.error ?? tokenQuery.error,
  };
}

function pageUsersRequest(searchText: string, page: number, others: UsagePagePlan['others']) {
  if (searchText) {
    const searchMode = searchText.includes('@') ? 'email' : 'name';
    const filter = teamsPeopleApiFilter({ query: searchText, searchMode, statuses: [] });
    return { page, pageSize: PAGE_SIZE, filter };
  }
  return { page: 0, pageSize: others?.windowSize ?? 1 };
}

function usersOfSeats(users: IamData[], seatIds: string[]) {
  const byId = new Map(users.map((user) => [user.itemId, user]));
  return seatIds.flatMap((id) => byId.get(id) ?? []);
}

function usersWithoutSeat(
  users: IamData[],
  others: UsagePagePlan['others'],
  filter: TeamsUsageFilter,
  holderIds: Set<string>,
  tokensOf: Map<string, number>
) {
  if (!others) return [];
  const withoutSeat = users.filter(
    (user) => !holderIds.has(user.itemId) && (filter !== 'not_using' || !tokensOf.get(user.itemId))
  );
  return withoutSeat.slice(others.from, others.from + others.count);
}
